import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableCellProps,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core';
import React, {ReactNode} from 'react';
import './MinGridTable.scss';
import Loader from '@components/framework/loader/Loader';
import {useTranslation} from 'react-i18next';

interface Row {
  render?(): React.ReactNode;
}

type fieldOrRender<TType> =
  | {field: keyof TType; render?: never}
  | {
      render: (val: TType) => ReactNode;
      field?: never;
    };

export type ColumnDef<TType> = {
  id?: string | number;
  label: string;
  colHeaderCellProps?: TableCellProps;
  colValueCellProps?: TableCellProps;
} & fieldOrRender<TType>;

interface IPaymentsTableProps<TType> {
  rows: (TType & Row)[];
  colDefs: ColumnDef<TType>[];
  className?: string;
  isLoading?: boolean;
  noDataText?: string;
  onRowClick?(row: TType): void;
  tableRowClassName?: string;
}

const MinGridTable = <TType,>({
  rows,
  colDefs,
  className = '',
  isLoading = false,
  noDataText,
  onRowClick,
  tableRowClassName
}: IPaymentsTableProps<TType>): JSX.Element => {
  const {t} = useTranslation();
  const totalColumnNumber = colDefs.length;
  const hasData = rows.length !== 0;
  return (
    <TableContainer
      component={Paper}
      className={['min-grid-table-container', className].join(' ')}>
      <Table stickyHeader>
        <TableHead>
          <TableRow>
            {colDefs.map((column, columnIndex) => (
              <TableCell
                key={column.id ?? columnIndex}
                {...column.colHeaderCellProps}>
                {column.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {isLoading && (
            <TableRow>
              <TableCell colSpan={totalColumnNumber} align="center">
                <Loader />
              </TableCell>
            </TableRow>
          )}
          {!isLoading && !hasData && (
            <TableRow>
              <TableCell colSpan={totalColumnNumber} align="center">
                {noDataText ?? t('COMMON.NO_DATA_GRID')}
              </TableCell>
            </TableRow>
          )}
          {!isLoading &&
            hasData &&
            rows.map((row, rowIndex) =>
              row.render ? (
                row.render()
              ) : (
                <TableRow
                  key={rowIndex}
                  onClick={() => onRowClick && onRowClick(row)}
                  className={tableRowClassName}>
                  {colDefs.map((column, columnIndex) => {
                    if (column.field) {
                      return (
                        <TableCell
                          key={column.id ?? columnIndex}
                          {...column.colValueCellProps}>
                          {row[column.field]}
                        </TableCell>
                      );
                    }
                    if (column.render) {
                      return (
                        <TableCell
                          key={column.id ?? columnIndex}
                          {...column.colValueCellProps}>
                          {column.render(row)}
                        </TableCell>
                      );
                    }
                    return (
                      <TableCell
                        key={column.id ?? columnIndex}
                        {...column.colValueCellProps}>
                        {' '}
                      </TableCell>
                    );
                  })}
                </TableRow>
              )
            )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default MinGridTable;
